/* eslint-disable react/prop-types */
/* eslint-disable no-undef */
import React, { useEffect, useMemo, useState } from 'react';
import { InfoCircleOutlined, RightOutlined } from '@ant-design/icons';
import { Alert } from 'antd';

import IframeModalView from '@/components/IframeModalView';
import { DataTable } from '../../../ui';
import { boardVisibleActivity } from '@/services/athenaBoard';
import { visibleActivityColumns } from '../columns/visibleActivityColumns';
import styles from '../board.less';

/**
 * 3. 看得见活动
 * ------------------------------------------------------------------
 * 自取数（不做容器预取）：排序与分页要改请求参数，必须放在本 pane 里，
 * 也才能配合外层 LazyMount。
 *
 * 表格用 DataTable（列定义在 columns/visibleActivityColumns.js）：它原先接不了
 * 「受控服务端排序」，是因为 DataTable 没透传 onChange —— 2026-09-17 给它补上
 * onChange 后本表一并换过来，至此经营看板 5 页无裸 Table。
 */
export const ActivityPane = (props) => {
  const { tenantId, coachUserId, encryptTenantId } = props;
  const [h5Visible, setH5Visible] = useState(false);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  /* 源 sort 语义：0 = order by used desc，非 0 = 升序（不是「不排序」）。
     排序与分页都必须走服务端：本地 sorter 只能排当页，跨页会错。 */
  const [sort, setSort] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    if (!tenantId || !coachUserId) return;
    setLoading(true);
    setError('');
    boardVisibleActivity({
      tenantId,
      coachUserId,
      sort,
      currentPage: page,
      pageSize: PAGE_SIZE,
    })
      .then((r) => {
        if (r && r.code === '00000') setData(r.data || {});
        else throw new Error((r && r.msg) || '看得见活动加载失败');
      })
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [tenantId, sort, page, coachUserId, retryKey]);

  const rows = data.memberList || [];
  /* 列定义是 (sort) 的工厂：用 useMemo 固定引用，否则每次渲染都是新数组，
     DataTable 内部对列归一化的 memo 会失效（它自己的注释里就提醒过这一点）。 */
  const columns = useMemo(() => visibleActivityColumns(sort), [sort]);

  /**
   * 入口点击 → 弹出手机宽 iframe（即原 IframeModalView，弹窗标题「数据中心」）。
   * 地址优先取接口下发的 data.h5Url（h5 侧 toThirdAppWebview(h5Url) 的同款）；
   * 预览/mock 下没有就兜底 datacenter 的 dataType=3 深链（= 看得见活动 tab）。
   */
  const h5Src =
    data.h5Url ||
    `${UMI_MINI_H5HOST}/litta-h5/#/datacenter?from=palmer` +
      `&encryptTenantId=${encryptTenantId || ''}&dataType=3`;

  return (
    <div>
      {/* 图8 反馈：入口与数据更新时间并成一行——
          时间在左、给底色突出；入口在右 */}
      <div className={styles.activityBar}>
        {data.updateTime && (
          <span className={styles.updateTime}>
            <InfoCircleOutlined /> 数据更新时间：{data.updateTime}
          </span>
        )}
        <a className={styles.activityBanner} onClick={() => setH5Visible(true)}>
          看得见活动，助力商家服务升单
          <RightOutlined />
        </a>
      </div>

      <IframeModalView
        title="数据中心"
        src={h5Src}
        visible={h5Visible}
        setVisible={setH5Visible}
      />

      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          action={
            <a onClick={() => setRetryKey((current) => current + 1)}>重试</a>
          }
        />
      )}

      <DataTable
        loading={loading}
        rowKey="userId"
        size="small"
        columns={columns}
        dataSource={rows}
        emptyText="暂无会员参与该活动"
        emptyHint="活动开始后，参与会员会出现在这里"
        onChange={(pagination, filters, sorter) => {
          const nextSort = sorter && sorter.order === 'ascend' ? 1 : 0;
          const nextPage =
            pagination && pagination.current ? pagination.current : 1;
          if (nextSort !== sort) setSort(nextSort);
          if (nextPage !== page) setPage(nextPage);
        }}
        pagination={{
          current: data.currentPage || page,
          pageSize: PAGE_SIZE,
          total: data.total || 0,
          showSizeChanger: false,
        }}
      />
    </div>
  );
};

export default ActivityPane;
